import { useContext, useEffect, useMemo, useReducer } from 'react';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import { RESET } from 'common/actions/row-groups/types';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import {
  RANGE_SENSITIVITY_REFERENCE_TITLE,
  RANGE_SENSITIVITY_REFERENCE_TYPE,
} from 'common/constants/documents';
import { RANGE_SENSITIVITY_PAGE_KEY, RANGE_SENSITIVITY_PAGE_VALUE } from 'common/constants/notes';
import { useFormat } from 'common/hooks';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { UseFormatValues } from 'common/types/format';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport/constants';
import CustomScalarSpreadsheet from 'components/CustomScalarSpreadsheet/CustomScalarSpreadsheet';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from 'components/Drawer/widget-drawer/WidgetDrawer.props';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { useLayoutContext } from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useDocuments } from 'services/hooks';
import { useNotes } from 'services/hooks/notes';
import useSpreadsheetNotes from 'services/hooks/spreadsheetNotes/useSpreadsheetNotes';
import { getArrayValue, getStringValue } from 'utilities';
import { getRangeSensitivityRowGroupsInitialState } from './config/customRowConfig';
import { getRangeSensitivityPctWorkbookKey, RANGE_SENSITIVITY_SPREADSHEET_NAME } from './constants';
import { RangeSensitivityProps } from './types';
import { File as ValuationFile } from '../../types';
import ValuationContext from '../../ValuationContext';

const RangeSensitivity = ({
  spreadsheets,
  onChange,
  workbook,
  rangeSensitivity,
  spreadsheetWorkbookKey,
  allocation,
  securitiesCount,
  isDisabled,
  isLoading,
  isSaving,
}: RangeSensitivityProps) => {
  const {
    measurementDate,
    setNotesInRangeSensitivity,
    setFilesInRangeSensitivity,
    setSpreadsheetNotesInRangeSensitivity,
  } = useContext(ValuationContext);
  const { isExportMode } = useLayoutContext();
  const featureId = rangeSensitivity.id ?? rangeSensitivity.panel_id;
  const spreadsheetNotesFeatureId = featureId == null ? undefined : Number(featureId);
  const approachPanelId = getStringValue(rangeSensitivity.panel_id);
  const { filesToSave, setFilesToSave } = useDocuments();
  const { notes, setNotes, notesHasChanged, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const {
    spreadsheetNotes,
    setSpreadsheetNotes,
    hasChanged: spreadsheetNotesHasChanged,
    handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
  } = useSpreadsheetNotes();
  const [format, formatDispatch] = useFormat({ page: CAP_TABLE_CURRENCY_PAGE, units: '' }) as UseFormatValues;

  const rangeSensitivitySheet =
    spreadsheets[spreadsheetWorkbookKey as keyof typeof spreadsheets] ??
    spreadsheets[RANGE_SENSITIVITY_SPREADSHEET_NAME] ??
    Object.values(spreadsheets)[0];

  const pctWorkbookKey = getRangeSensitivityPctWorkbookKey(
    getStringValue(rangeSensitivity.panel_id)
  );
  const sensitivityPctSheet = spreadsheets[pctWorkbookKey as keyof typeof spreadsheets];

  const weightedShareValues = useMemo(
    () => getArrayValue(allocation?.weighted_share_values),
    [allocation?.weighted_share_values]
  );

  const nonEmptyWeightedShareValues = useMemo(
    () => weightedShareValues?.filter(wsw => getArrayValue(wsw.share_values)?.length > 0),
    [weightedShareValues]
  );

  const rowGroupsInitialState = useMemo(
    () => getRangeSensitivityRowGroupsInitialState({ securitiesCount, nonEmptyWeightedShareValues }),
    [securitiesCount, nonEmptyWeightedShareValues]
  );

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, rowGroupsInitialState);

  useEffect(() => {
    setRowGroups({ type: RESET, payload: rowGroupsInitialState });
  }, [rowGroupsInitialState]);

  useEffect(() => {
    if (!isEmpty(notes) && setNotesInRangeSensitivity) {
      setNotesInRangeSensitivity(prevState => {
        const tmpNotes = prevState.filter(note => note.panelId !== approachPanelId);

        return [
          ...tmpNotes,
          {
            notes: notes ?? undefined,
            panelId: approachPanelId,
            notesHasChanged,
          },
        ];
      });
    }
  }, [notes, notesHasChanged, approachPanelId, setNotesInRangeSensitivity]);

  useEffect(() => {
    if (!setSpreadsheetNotesInRangeSensitivity) {
      return;
    }
    setSpreadsheetNotesInRangeSensitivity(prevState => {
      const tmpEntries = prevState.filter(entry => entry.panelId !== approachPanelId);

      if (isEmpty(spreadsheetNotes)) {
        return tmpEntries;
      }

      return [
        ...tmpEntries,
        {
          notes: spreadsheetNotes,
          panelId: approachPanelId,
          hasChanged: spreadsheetNotesHasChanged,
        },
      ];
    });
  }, [spreadsheetNotes, spreadsheetNotesHasChanged, approachPanelId, setSpreadsheetNotesInRangeSensitivity]);

  useEffect(() => {
    if (!setFilesInRangeSensitivity) {
      return;
    }
    setFilesInRangeSensitivity(prevState => {
      const tmpFiles = prevState.filter(file => file.panelId !== approachPanelId);

      if (isEmpty(filesToSave)) {
        return tmpFiles;
      }

      return [
        ...tmpFiles,
        {
          filesToSave: filesToSave as ValuationFile['filesToSave'],
          panelId: approachPanelId,
        },
      ];
    });
  }, [filesToSave, approachPanelId, setFilesInRangeSensitivity]);

  const sheetProps = useMemo(() => {
    if (rangeSensitivitySheet) {
      return {
        ...rangeSensitivitySheet,
        onChange,
        rowGroups,
        setRowGroups,
        sheet: rangeSensitivitySheet,
        workbook,
        format,
        formatDispatch,
        showToolbar: true,
      } as unknown as typeof ScalarSpreadsheet;
    }
    return null;
  }, [rangeSensitivitySheet, onChange, rowGroups, setRowGroups, workbook, format, formatDispatch]);

  const pctSheetProps = useMemo(() => {
    if (sensitivityPctSheet) {
      return {
        ...sensitivityPctSheet,
        onChange,
        sheet: sensitivityPctSheet,
        workbook,
        showToolbar: false,
      } as unknown as typeof ScalarSpreadsheet;
    }
    return null;
  }, [sensitivityPctSheet, onChange, workbook]);

  let rangeSensitivityExportStatus: 'loading' | 'ready' = 'ready';
  if (!rangeSensitivitySheet?.data) {
    rangeSensitivityExportStatus = 'loading';
  } else if (sensitivityPctSheet && !sensitivityPctSheet.data) {
    rangeSensitivityExportStatus = 'loading';
  }
  useExportSignal({
    target: PdfExportTargets.RANGE_SENSITIVITY.id,
    status: rangeSensitivityExportStatus,
    enabled: isExportMode,
  });

  return (
    <Grid container spacing={2}>
      <Grid
        data-ref={PdfExportTargets.RANGE_SENSITIVITY.id}
        size={{ xs: 12 }}
        style={isExportMode ? EXPORT_MODE_STYLES : {}}>
        <Grid container spacing={2}>
          <Grid data-testid="range-sensitivity-spreadsheet" size={{ xs: 12 }}>
            <CustomScalarSpreadsheet {...sheetProps} isLoading={isLoading} isSaving={isSaving} />
          </Grid>

          {pctSheetProps && (
            <Grid
              data-testid="range-sensitivity-pct-spreadsheet"
              size={{ xs: 12, sm: 5, md: 4 }}>
              <CustomScalarSpreadsheet {...pctSheetProps} isLoading={isLoading} isSaving={isSaving} />
            </Grid>
          )}
        </Grid>
      </Grid>

      <WidgetDrawer
        applicationContext={WidgetDrawerApplicationContext.RANGE_SENSITIVITY}
        notesProps={{
          pageType: RANGE_SENSITIVITY_PAGE_VALUE,
          pageTypeKey: RANGE_SENSITIVITY_PAGE_KEY,
          pageTypeId: spreadsheetNotesFeatureId,
          notes,
          isApproach: true,
          setNotes,
          onAddNote,
          onUpdateNotes,
          onDeleteNote,
          isDisabled,
        }}
        documentReferencesProps={{
          selectedMeasurementDate: measurementDate,
          referenceType: RANGE_SENSITIVITY_REFERENCE_TYPE,
          currentPage: RANGE_SENSITIVITY_REFERENCE_TITLE,
          referencedFeatureId: featureId,
          filesToSave,
          setFilesToSave,
          isDisabled,
        }}
        spreadsheetNotesProps={{
          pageType: RANGE_SENSITIVITY_PAGE_VALUE,
          pageTypeKey: RANGE_SENSITIVITY_PAGE_KEY,
          pageTypeId: spreadsheetNotesFeatureId,
          notes: spreadsheetNotes,
          setNotes: setSpreadsheetNotes,
          handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
          isApproach: true,
          isDisabled,
        }}
      />
    </Grid>
  );
};

export default RangeSensitivity;
